import {useState, useEffect} from 'react'

import { GridColDef, useGridApiRef } from '@mui/x-data-grid';

import { adminAPI } from '../../../api';
import { convertToDateTime, convertToDateTimeWithSeconds, StyledDataGrid, dataGridCellClassName, dataGridHeaderClassName } from '../../../utils'

import { UserTypeUpdateModal } from './UserTypeUpdateModal';

import toastr from 'toastr';

import { UserType } from '../../../models';
import { useAuth } from '../../../context/auth';

interface Props {
}

const Users : React.FC<Props> = ({}) => {

  const [usersLoaded, setUsersLoaded] = useState<boolean>(false)
  const [users, setUsers] = useState<any[]>([])
  const [otpSentHistoryLoaded, setOtpSentHistoryLoaded] = useState<boolean>(false)
  const [otpSentHistory, setOtpSentHistory] = useState<any[]>([])
  const [showUserTypeUpdateModal, setShowUserTypeUpdateModal] = useState<boolean>(false)
  const apiRef = useGridApiRef();

  const {currentUser} = useAuth()

  useEffect(() => {
    loadUsers()
    loadOTPSentHistory()
  }, [])

  async function loadUsers(){
    let {data: data} = await adminAPI.getUsers()
    setUsers(data['data'])
    setUsersLoaded(true)
  }

  async function loadOTPSentHistory(){
    let {data: data} = await adminAPI.getOTPSentHistory()
    setOtpSentHistory(data['data'])
    setOtpSentHistoryLoaded(true)
  }

  const handleUserTypeUpdated = async () => {
    await loadUsers()
    toastr.info("User type has been updated. The user(s) may need to re-login for the changes to take effect.")
  }

  const columnsForUsers: GridColDef[] = [
    { field: 'type', headerName: 'Type', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 130,
      valueGetter: (value) => value || 'user'
    },
    { field: 'id', headerName: 'ID', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 300},
    { field: 'created_dt', headerName: 'Signed-up on', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 170,
      renderCell: (params) => {
        return convertToDateTime(params.value)
      }
    },
    { field: 'latest_sign_in_on', headerName: 'Last signed-in on', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 170,
      renderCell: (params) => {
        return convertToDateTime(params.value)
      }
    },
    { field: 'number_of_chats', headerName: '# chats', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName + '', minWidth:150, flex:1,
      renderCell: (params) => {
        return params.value ? params.value.toLocaleString() : 0
      }
    },
    { field: 'number_of_prompts', headerName: '# prompts', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName + '', minWidth:150, flex:1,  
      renderCell: (params) => {
        return params.value ? params.value.toLocaleString() : 0
      }
    },
    { field: 'number_of_responses', headerName: '# responses', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName + '', minWidth:150, flex:1,
      renderCell: (params) => {
        return params.value ? params.value.toLocaleString() : 0
      }
    },
    { field: 'latest_response_on', headerName: 'Latest response on', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 170,
      renderCell: (params) => {
        return params.value ? convertToDateTime(params.value) : '-'
      }
    }
  ];
  const columnsForOTPSentHistory: GridColDef[] = [
    { field: 'created_dt', headerName: 'Requested on', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 170,  flex:1,
      renderCell: (params) => {
        return convertToDateTimeWithSeconds(params.value)
      }
    },
    { field: 'sent_dt', headerName: 'Sent on', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 170,flex:1,
      renderCell: (params) => {
        return convertToDateTimeWithSeconds(params.value)
      }
    },
    { field: 'used_dt', headerName: 'Used on', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 170,flex:1,
      renderCell: (params) => {
        return convertToDateTimeWithSeconds(params.value)
      }
    },
  ];

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body pt-5">
            <div className="d-flex justify-content-between mt-5 flex-end">
              <div className="fw-semibold fs-6">Users</div>

              { currentUser?.type === UserType.ADMIN &&
              <div className="d-flex flex-row flex-center">
                <button className="btn btn-sm btn-light-dark" onClick={()=>setShowUserTypeUpdateModal(true)}>
                  Update User Type
                </button>
                {showUserTypeUpdateModal && <UserTypeUpdateModal show={showUserTypeUpdateModal} handleClose={()=>setShowUserTypeUpdateModal(false)} handleUpdated={handleUserTypeUpdated}/>}
              </div>
              }
            </div>
            {usersLoaded && (
              <StyledDataGrid
                apiRef={apiRef}
                getRowId={(row) => row['id']}
                rows={users}
                columns={columnsForUsers}
                disableColumnMenu={false}
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 50 },
                  },
                  columns: {
                    columnVisibilityModel: {'references':false},
                  }
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                  },
                }}
                pageSizeOptions={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100]}
              />
            )}
          </div>
        </div>
      </div>
      <div className="col-12 mt-5">
        <div className="card">
          <div className="card-body pt-5">
            <div className="fw-semibold fs-6">OTP Sent History</div>
            {usersLoaded && (
              <StyledDataGrid
                apiRef={apiRef}
                getRowId={(row) => row.id}
                rows={otpSentHistory}
                columns={columnsForOTPSentHistory}
                disableColumnMenu={false}
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 50 },
                  },
                  columns: {
                    columnVisibilityModel: {'references':false},
                  }
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                  },
                }}
                pageSizeOptions={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100]}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Users